'use client';
import { cn } from '@/app/lib/utils';
import React from 'react';

export type PillVariant = 'soft' | 'outline' | 'text' | 'solid';
export type PillColor =
  | 'default'
  | 'success'
  | 'info'
  | 'warning'
  | 'neutral'
  | 'destructive'
  | 'purple'
  | 'cyan'
  | 'sky'
  | 'indigo'
  | 'fuchsia'
  | 'teal';

export type PillProps = React.HTMLAttributes<HTMLElement> & {
  children?: React.ReactNode;
  color?: PillColor;
  variant?: PillVariant;
  size?: 'sm' | 'md' | 'lg';
  icon?: React.ReactNode;
  className?: string;
  rounded?: boolean;
  style?: React.CSSProperties;
  isBadge?: boolean;
  title?: string;
  hasDot?: boolean;
  onClick?: () => void;
  opaque?: boolean;
  as?: 'div' | 'span';
};

const opaqueSoftBackgrounds: Record<PillColor, string> = {
  default: 'var(--muted)',
  success: 'color-mix(in oklab, var(--success) 10%, var(--background))',
  info: 'color-mix(in oklab, var(--primary) 10%, var(--background))',
  warning: 'color-mix(in oklab, var(--warning) 10%, var(--background))',
  neutral: 'var(--muted)',
  destructive: 'color-mix(in oklab, var(--destructive) 10%, var(--background))',
  purple: 'color-mix(in oklab, rgb(168 85 247) 10%, var(--background))',
  cyan: 'color-mix(in oklab, rgb(6 182 212) 10%, var(--background))',
  sky: 'color-mix(in oklab, rgb(2 132 199) 10%, var(--background))',
  indigo: 'color-mix(in oklab, rgb(99 102 241) 10%, var(--background))',
  fuchsia: 'color-mix(in oklab, rgb(217 70 239) 10%, var(--background))',
  teal: 'color-mix(in oklab, rgb(20 184 166) 10%, var(--background))',
};

const pillStyles: Record<PillVariant, Record<string, string>> = {
  soft: {
    default: 'bg-border text-foreground',
    success: 'bg-success/10 text-success',
    info: 'bg-primary/10 text-primary',
    warning: 'bg-warning/10 text-warning',
    neutral: 'bg-border text-foreground',
    destructive: 'bg-destructive/10 text-destructive',
    purple: 'bg-purple-500/10 text-purple-500',
    cyan: 'bg-cyan-500/10 text-cyan-500',
    sky: 'bg-sky-600/10 text-sky-600',
    indigo: 'bg-indigo-500/10 text-indigo-500',
    fuchsia: 'bg-fuchsia-500/10 text-fuchsia-500',
    teal: 'bg-teal-500/10 text-teal-600 dark:text-teal-400',
  },
  outline: {
    default: 'border border-border bg-background text-foreground',
    success: 'border border-success text-success',
    info: 'border border-primary text-primary',
    warning: 'border border-warning text-warning',
    neutral: 'border border-gray-500 text-gray-500',
    destructive: 'border border-destructive text-destructive',
    purple: 'border border-purple-500 text-purple-500',
    cyan: 'border border-cyan-500 text-cyan-500',
    sky: 'border border-sky-600 text-sky-600',
    indigo: 'border border-indigo-500 text-indigo-500',
    fuchsia: 'border border-fuchsia-500 text-fuchsia-500',
    teal: 'border border-teal-500 text-teal-600 dark:border-teal-400 dark:text-teal-300',
  },
  solid: {
    default: 'bg-black text-white',
    success: 'bg-success text-white',
    info: 'bg-primary text-white',
    warning: 'bg-warning text-white',
    neutral: 'bg-secondary text-white',
    destructive: 'bg-destructive text-white',
    purple: 'bg-purple-500 text-white',
    cyan: 'bg-cyan-500 text-white',
    sky: 'bg-sky-600 text-white',
    indigo: 'bg-indigo-500 text-white',
    fuchsia: 'bg-fuchsia-500 text-white',
    teal: 'bg-teal-600 text-white',
  },
  text: {
    default: 'text-foreground',
    success: 'text-success',
    info: 'text-primary',
    warning: 'text-warning',
    neutral: 'text-gray-500',
    destructive: 'text-destructive',
    purple: 'text-purple-500',
    cyan: 'text-cyan-500',
    sky: 'text-sky-600',
    indigo: 'text-indigo-500',
    fuchsia: 'text-fuchsia-500',
    teal: 'text-teal-600 dark:text-teal-400',
  },
};

const pillSizeStyles: Record<NonNullable<PillProps['size']>, string> = {
  sm: 'h-5 px-1.5 py-0.5 text-[12px] leading-[16px]',
  md: 'h-[22px] px-1.5 py-0.5 text-[12px] leading-[16px]',
  lg: 'h-[26px] px-3 py-1 text-sm leading-4',
};

export default function Pill({
  children,
  color = 'default',
  variant = 'soft',
  size = 'md',
  icon,
  className = '',
  isBadge = false,
  rounded = true,
  style = {},
  hasDot = false,
  title,
  onClick,
  opaque = false,
  as: Component = 'div',
  ...rest
}: PillProps) {
  return (
    <Component
      onClick={onClick}
      className={cn(
        'inline-flex w-fit items-center justify-center whitespace-nowrap font-medium',
        pillSizeStyles[size],
        icon ? 'gap-0.5' : 'gap-1',
        pillStyles[variant][color] || pillStyles[variant].default,
        rounded ? 'rounded-full' : 'rounded-[6px]',
        isBadge && 'h-[14px] w-[14px] rounded-full p-[2px] text-[11px] font-normal leading-3',
        hasDot && 'px-2',
        className
      )}
      style={{
        ...(opaque && variant === 'soft'
          ? { background: opaqueSoftBackgrounds[color] }
          : undefined),
        ...style,
      }}
      title={title}
      {...rest}
    >
      {icon && <span className="flex items-center shrink-0">{icon}</span>}
      {children}
    </Component>
  );
}
